// src/generators/dzial1_liczby_i_dzialania/temat1_dzialania/podtemat3_zbiory_types/InclusionExclusionUnionTask.js
import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const rand = (a, b) => a + Math.floor(Math.random() * (b - a + 1));

/* ======================================================================
   Losowanie przecięcia k = |A∩B| z szerokim, realistycznym rozkładem.
   Tutaj jako "U" traktujemy |A∪B| (liczbę uczniów, którzy spełniają
   przynajmniej jedno z dwóch kryteriów).
   ====================================================================== */
function sampleKWide(U) {
  const r = Math.random();
  const half = Math.floor(0.5 * U);
  if (r < 0.05) {
    return U; // 5%: wszyscy w obu (maksymalne przecięcie)
  } else if (r < 0.20) {
    // 15%: duże przecięcie 50%..100% U
    return rand(Math.min(half + 1, U), U);
  } else if (r < 0.85) {
    // 65%: małe/średnie przecięcie 0..50% U
    return rand(0, half);
  } else {
    // 15%: środek rozkładu ~25%..75% U
    const q25 = Math.floor(0.25 * U);
    const q75 = Math.floor(0.75 * U);
    return rand(q25, Math.max(q25, q75));
  }
}

/* ===== generator danych dla sumy |A∪B| =====
   Chcemy:
     - U = |A∪B|: liczba uczniów w klasie (wszyscy spełniają ≥1 warunek),
     - k = |A∩B|,
     - |A|, |B| w sensownych zakresach.

   Warunek spójności:
     U = |A∪B| = |A| + |B| − |A∩B|.

   W treści NIE podajemy U – uczeń ma je policzyć z wzoru.
*/
function genNumbers() {
  const minCount = 8;

  while (true) {
    // U = liczba uczniów spełniających przynajmniej jeden warunek
    // (i jednocześnie liczba uczniów w klasie).
    const U = rand(24, 34);

    const k = sampleKWide(U);

    // A musi być co najmniej k (bo A zawiera przecięcie) i co najmniej minCount
    // B = U − A + k musi być w [minCount, U]
    const A_min = Math.max(minCount, k);
    const A_max = Math.min(U, U + k - minCount);

    if (A_min > A_max) continue;

    const A = rand(A_min, A_max);
    const B = U - A + k;

    if (A >= minCount && A <= U && B >= minCount && B <= U) {
      const inter = k;
      const union = U;
      return { A, B, inter, union };
    }
  }
}

export function generateTask() {
  // Scenariusze: tylko social media i przedmioty rozszerzone.
  const scenario = pick(['social', 'extensions']);
  const { A, B, inter, union } = genNumbers();

  let title, aLabel, bLabel, who, noun;

  if (scenario === 'social') {
    title = 'Konta w social media';
    aLabel = 'ma konto na TikToku';
    bLabel = 'ma konto na Instagramie';
    who = 'uczniów';
    noun = 'kontach';
  } else {
    title = 'Przedmioty rozszerzone';
    // trzy realistyczne pary rozszerzeń (dopełniacz):
    // (1) matematyki + fizyki
    // (2) języka polskiego + historii
    // (3) chemii + biologii
    const pairs = [
      ['matematyki', 'fizyki'],
      ['języka polskiego', 'historii'],
      ['chemii', 'biologii'],
    ];
    const [s1, s2] = pick(pairs);
    aLabel = `zdaje maturę rozszerzoną z ${s1}`;
    bLabel = `zdaje maturę rozszerzoną z ${s2}`;
    who = 'uczniów';
    noun = 'przedmiotach rozszerzonych';
  }

  return {
    type: 'InclusionExclusionUnionTask',
    data: { scenario, title, A, B, inter, union, aLabel, bLabel, who, noun }
  };
}

export function renderTask({ data }) {
  const { title, A, B, inter, aLabel, bLabel, who, scenario } = data;

  const extraInfo =
    scenario === 'social'
      ? 'Wiadomo, że każdy uczeń ma przynajmniej jedno z tych kont (albo TikTok, albo Instagram, albo oba).'
      : 'Wiadomo, że każdy uczeń zdaje maturę rozszerzoną z przynajmniej jednego z tych przedmiotów (albo z pierwszego, albo z drugiego, albo z obu).';

  const question =
    scenario === 'social'
      ? 'Ilu uczniów ma przynajmniej jedno z tych kont?'
      : 'Ilu uczniów zdaje maturę rozszerzoną z co najmniej jednego z tych przedmiotów?';

  const intersectionLine =
    scenario === 'social'
      ? `– ${inter} ${who} posiada oba te konta.`
      : `– ${inter} ${who} rozszerza oba te przedmioty.`;

  return (
    <div>
      <p><strong>{title}</strong></p>

      <p>
        W pewnej klasie przeprowadzono ankietę. Z ankiety wynika, że:
        <br />– <InlineMath math={String(A)} /> {who} {aLabel},
        <br />– <InlineMath math={String(B)} /> {who} {bLabel},
        <br />{intersectionLine}
      </p>

      <p>{extraInfo}</p>
      <p>{question}</p>
    </div>
  );
}

/* === INPUT: jak w innych zadaniach z tego podtematu === */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=''
      placeholder='np. 24'
    />
  );
}

/* === CHECK: porównujemy z |A∪B| === */
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const given = userAnswer.parsed;
  if (given == null) return false;
  return Math.abs(Number(given) - Number(data.union)) < 1e-9;
}

export function renderAnswer({ data }) {
  return <InlineMath math={String(data.union)} />;
}

/* === WYJAŚNIENIE: dla sumy === */
export function renderExplanation({ data }) {
  const { A, B, inter, union } = data;
  const sum = A + B;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12, textAlign: 'left' }}>
      <p><strong>Krok 1:</strong> Korzystamy ze wzoru włączeń–wyłączeń dla sumy:</p>
      <BlockMath math={'|A \\cup B| = |A| + |B| - |A \\cap B|'} />

      <p>
        <strong>Krok 2:</strong> Podstawiamy liczby z treści:
      </p>
      <BlockMath math={`|A| = ${A},\\quad |B| = ${B},\\quad |A \\cap B| = ${inter}`} />

      <p>
        <strong>Krok 3 (intuicja):</strong>{' '}
        <InlineMath math={`|A| + |B| = ${A} + ${B} = ${sum}`} />{' '}
        liczy osoby w obu zbiorach, ale tych z przecięcia
        <InlineMath math={'\\, A \\cap B \\, '} /> liczymy podwójnie,
        więc trzeba je raz odjąć.
      </p>

      <p><strong>Krok 4:</strong> Liczymy sumę:</p>
      <BlockMath math={`|A \\cup B| = ${A} + ${B} - ${inter} = ${union}`} />

      <p><strong>Odpowiedź:</strong> <InlineMath math={String(union)} />.</p>
    </div>
  );
}

/* === Podgląd odpowiedzi użytkownika === */
export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object' || !('raw' in userAnswer)) {
    return <i>(brak odpowiedzi)</i>;
  }
  const latex = formatLatexPreview(userAnswer.raw);
  return <InlineMath math={latex || ''} />;
}
